/**
 * Patient grade and its policy engine (owner request: "grade patients and
 * train patients to respect you"; owner-research schedule (e) and money (e)).
 *
 * - The grade: an attendance sub-score and a financial sub-score (0-100,
 *   each with its own letter) and an overall A-F letter, recomputed nightly
 *   from synced PMS data by a plain formula (`@velora/domain/patient-grade`).
 *   Staff see the letter and its reasons; patients never do.
 * - The policy engine: per-office rules driven by the grade. The whole
 *   engine and every rule are OFF until an administrator turns them on.
 *   Fees and deposits have their own switch, off until the practice's
 *   counsel has reviewed state dental board and consumer-fee rules.
 *
 * Routes live under `/v1/patient-grade`.
 */
import { z } from 'zod';

export const GRADE_LETTERS = ['A', 'B', 'C', 'D', 'F'] as const;
export const GradeLetterSchema = z.enum(GRADE_LETTERS);
export type GradeLetterValue = z.infer<typeof GradeLetterSchema>;

export const GRADE_PERMISSIONS = Object.freeze({
  /** See the badge and its reasons: everyone who sees the patient. */
  view: 'patient.view',
  /** Mark an event excused (an accommodation, transport, an office error). */
  excuse: 'patient.edit',
  /** Override a grade, restore to B, recompute, read the disparity report. */
  manage: 'patient.grade_manage',
  /** Office policy settings. */
  settings: 'settings.manage',
  /** Send a policy explanation to the patient. */
  message: 'comms.send',
});

/* ------------------------------------------------------------------------ */
/* Policy settings (per office)                                              */
/* ------------------------------------------------------------------------ */

export const POLICY_RULE_KEYS = [
  'primeTime',
  'onlineBooking',
  'confirmation',
  'cardOnFile',
  'deposit',
  'paymentAtService',
  'inHousePlan',
  'patientMessages',
] as const;
export type PolicyRuleKey = (typeof POLICY_RULE_KEYS)[number];

const LetterList = z.array(GradeLetterSchema).max(5);

export const ONLINE_BOOKING_MODES = ['yes', 'off_peak', 'no'] as const;
export type OnlineBookingMode = (typeof ONLINE_BOOKING_MODES)[number];

export const CONFIRMATION_CADENCES = [
  'light',
  'standard',
  'extra_call_48h',
  'call_release_24h',
  'phone_only',
] as const;
export type ConfirmationCadence = (typeof CONFIRMATION_CADENCES)[number];

export const CADENCE_LABEL: Readonly<Record<ConfirmationCadence, string>> = Object.freeze({
  light: 'Light: one text',
  standard: 'Standard reminders',
  extra_call_48h: 'Extra reminder and a call at 48 hours',
  call_release_24h: 'Call to confirm; offer the time to others if not confirmed 24 hours before',
  phone_only: 'Must confirm by phone',
});

const ByGrade = <T extends z.ZodType>(t: T) => z.object({ A: t, B: t, C: t, D: t, F: t });

export const GradePolicyRulesSchema = z.object({
  /** C, D and F (by default) are not offered prime-time slots in booking and Find Openings. */
  primeTime: z.object({ on: z.boolean(), grades: LetterList }),
  /** Online self-booking per grade: any time, off-peak only, or call the office. */
  onlineBooking: z.object({ on: z.boolean(), byGrade: ByGrade(z.enum(ONLINE_BOOKING_MODES)) }),
  /** Confirmation cadence per grade (the reminders engine reads it). */
  confirmation: z.object({ on: z.boolean(), byGrade: ByGrade(z.enum(CONFIRMATION_CADENCES)) }),
  /** A card on file (with written consent) is required to book. */
  cardOnFile: z.object({ on: z.boolean(), grades: LetterList }),
  /**
   * A deposit for visits of at least `minMinutes` (0 = every visit, null =
   * never). Also needs `feesDepositsEnabled` (legal review).
   */
  deposit: z.object({
    on: z.boolean(),
    byGrade: ByGrade(z.number().int().min(0).max(600).nullable()),
  }),
  /** Estimated patient portion due at the time of service. */
  paymentAtService: z.object({ on: z.boolean(), grades: LetterList }),
  /** Grades eligible for an in-house payment plan (others: third-party financing). */
  inHousePlan: z.object({ on: z.boolean(), eligibleGrades: LetterList }),
  /** Explain an applied rule to the patient (policy wording, never the letter). */
  patientMessages: z.object({ on: z.boolean() }),
});
export type GradePolicyRules = z.infer<typeof GradePolicyRulesSchema>;

/** Prime time: the first and last part of each day and (optionally) Saturdays. */
export const PrimeTimeSchema = z.object({
  firstMinutes: z.number().int().min(0).max(240),
  lastMinutes: z.number().int().min(0).max(240),
  saturdays: z.boolean(),
});
export type PrimeTime = z.infer<typeof PrimeTimeSchema>;

export const GradePolicySchema = z.object({
  locationId: z.uuid(),
  /** The office's master switch for the whole engine. Off by default. */
  enabled: z.boolean(),
  rules: GradePolicyRulesSchema,
  /**
   * Fees and deposits stay off until legal review: missed-appointment fees
   * and deposits are never asked for while this is false, whatever the
   * rules say. Medicaid-only patients are never charged either way.
   */
  feesDepositsEnabled: z.boolean(),
  /** Who confirmed the legal review, and when. */
  feesDepositsConfirmedBy: z.string().max(200).nullable(),
  feesDepositsConfirmedAt: z.iso.datetime({ offset: true }).nullable(),
  primeTime: PrimeTimeSchema,
  updatedAt: z.iso.datetime({ offset: true }).nullable(),
  updatedByName: z.string().max(200).nullable(),
});
export type GradePolicy = z.infer<typeof GradePolicySchema>;

/** Every rule as the owner research suggests, but switched OFF. */
export const DEFAULT_POLICY_RULES: GradePolicyRules = Object.freeze({
  primeTime: { on: false, grades: ['C', 'D', 'F'] },
  onlineBooking: { on: false, byGrade: { A: 'yes', B: 'yes', C: 'off_peak', D: 'no', F: 'no' } },
  confirmation: {
    on: false,
    byGrade: {
      A: 'light',
      B: 'standard',
      C: 'extra_call_48h',
      D: 'call_release_24h',
      F: 'phone_only',
    },
  },
  cardOnFile: { on: false, grades: ['C', 'D', 'F'] },
  deposit: { on: false, byGrade: { A: null, B: null, C: null, D: 60, F: 0 } },
  paymentAtService: { on: false, grades: ['B', 'C', 'D', 'F'] },
  inHousePlan: { on: false, eligibleGrades: ['A', 'B', 'C'] },
  patientMessages: { on: false },
}) as GradePolicyRules;

export const DEFAULT_PRIME_TIME: PrimeTime = Object.freeze({
  firstMinutes: 60,
  lastMinutes: 60,
  saturdays: true,
});

export function defaultGradePolicy(locationId: string): GradePolicy {
  return {
    locationId,
    enabled: false,
    rules: structuredClone(DEFAULT_POLICY_RULES) as GradePolicyRules,
    feesDepositsEnabled: false,
    feesDepositsConfirmedBy: null,
    feesDepositsConfirmedAt: null,
    primeTime: { ...DEFAULT_PRIME_TIME },
    updatedAt: null,
    updatedByName: null,
  };
}

/**
 * Reads a stored rules object leniently: a rule missing or malformed falls
 * back to the default, which is OFF. Nothing turns on by accident.
 */
export function parseStoredRules(raw: unknown): GradePolicyRules {
  const base = structuredClone(DEFAULT_POLICY_RULES) as GradePolicyRules;
  if (!raw || typeof raw !== 'object') return base;
  const out: Record<string, unknown> = { ...base };
  for (const key of POLICY_RULE_KEYS) {
    const one = GradePolicyRulesSchema.shape[key].safeParse((raw as Record<string, unknown>)[key]);
    if (one.success) out[key] = one.data;
  }
  return out as GradePolicyRules;
}

export const GradePolicyPutSchema = z.object({
  locationId: z.uuid(),
  enabled: z.boolean(),
  rules: GradePolicyRulesSchema,
  feesDepositsEnabled: z.boolean(),
  /** Required (true) when turning fees and deposits on: "Our counsel has reviewed..." */
  legalReviewConfirmed: z.boolean().optional(),
  primeTime: PrimeTimeSchema,
});
export type GradePolicyPut = z.infer<typeof GradePolicyPutSchema>;

/* ------------------------------------------------------------------------ */
/* Grades                                                                    */
/* ------------------------------------------------------------------------ */

export const GradeReasonSchema = z.object({
  dimension: z.enum(['attendance', 'financial']),
  code: z.string().max(40),
  text: z.string().max(300),
  points: z.number(),
});
export type GradeReasonView = z.infer<typeof GradeReasonSchema>;

export const GradeOverrideViewSchema = z.object({
  letter: GradeLetterSchema,
  reason: z.string().max(500),
  byName: z.string().max(200),
  at: z.iso.datetime({ offset: true }),
  expiresAt: z.iso.datetime({ offset: true }).nullable(),
});

/**
 * The counts behind the attendance grade over the last 24 months (excused
 * events left out), shown on the schedule hover. Counts only.
 */
export const GradeAttendanceHistorySchema = z.object({
  /** Patient cancellations (short notice and with notice; office cancellations never count). */
  cancellations: z.number().int().min(0),
  /** Of those, under 48 hours notice or broken with no notice recorded. */
  shortNotice: z.number().int().min(0),
  noShows: z.number().int().min(0),
  /** Kept visits more than 10 minutes late. */
  lateArrivals: z.number().int().min(0),
  /** Kept visits with an arrival time (what late arrivals are out of). */
  timedVisits: z.number().int().min(0),
  /** "Tends to arrive late": 2+ late arrivals and late to at least 30% of timed visits. */
  likelyLate: z.boolean(),
  /** Usual minutes late (median of the late visits) when likely late. */
  typicalMinutesLate: z.number().int().min(0).nullable(),
});
export type GradeAttendanceHistory = z.infer<typeof GradeAttendanceHistorySchema>;

export const PatientGradeSchema = z.object({
  patientId: z.uuid(),
  /** The letter staff and policies use (an active override wins). */
  letter: GradeLetterSchema,
  computedLetter: GradeLetterSchema,
  attendance: z.object({ score: z.number().nullable(), letter: GradeLetterSchema }),
  financial: z.object({ score: z.number().nullable(), letter: GradeLetterSchema }),
  provisional: z.boolean(),
  reasons: z.array(GradeReasonSchema).max(30),
  /** When the letter last changed (computed or overridden). */
  changedAt: z.iso.datetime({ offset: true }).nullable(),
  previousLetter: GradeLetterSchema.nullable(),
  computedAt: z.iso.datetime({ offset: true }),
  override: GradeOverrideViewSchema.nullable(),
  formulaVersion: z.string().max(40),
  /** No grade row yet: shown as "B, provisional" until the nightly run. */
  ungraded: z.boolean(),
  /** The 24-month attendance counts; null before the first run of pg-2026.12.2. */
  history: GradeAttendanceHistorySchema.nullable().optional(),
});
export type PatientGrade = z.infer<typeof PatientGradeSchema>;

export const GradeListQuerySchema = z.object({
  /** Comma-separated patient ids (up to 200). */
  patientIds: z.string().regex(/^[0-9a-fA-F-]{36}(,[0-9a-fA-F-]{36}){0,199}$/),
});
export const GradeListSchema = z.object({ grades: z.array(PatientGradeSchema) });
export type GradeList = z.infer<typeof GradeListSchema>;

export const GRADE_EVENT_KINDS = [
  'computed',
  'override',
  'override_cleared',
  'restore_b',
  'excused',
  'excuse_revoked',
] as const;

export const GradeHistoryEntrySchema = z.object({
  id: z.uuid(),
  kind: z.enum(GRADE_EVENT_KINDS),
  fromLetter: GradeLetterSchema.nullable(),
  toLetter: GradeLetterSchema.nullable(),
  reason: z.string().max(500).nullable(),
  actorName: z.string().max(200),
  at: z.iso.datetime({ offset: true }),
});

export const EXCUSAL_SOURCES = [
  'appointment',
  'tx_case',
  'payment_plan',
  'payment',
  'ledger_entry',
] as const;
export const EXCUSAL_CATEGORIES = [
  'accommodation',
  'transport',
  'medical',
  'office_error',
  'weather',
  'other',
] as const;
export const EXCUSAL_CATEGORY_LABEL: Readonly<Record<(typeof EXCUSAL_CATEGORIES)[number], string>> =
  Object.freeze({
    accommodation: 'Disability accommodation',
    transport: 'Transport',
    medical: 'Illness or medical',
    office_error: 'Office error',
    weather: 'Weather or emergency',
    other: 'Other',
  });

export const ExcusalSchema = z.object({
  id: z.uuid(),
  source: z.enum(EXCUSAL_SOURCES),
  sourceId: z.uuid(),
  category: z.enum(EXCUSAL_CATEGORIES),
  reason: z.string().max(500),
  createdByName: z.string().max(200),
  createdAt: z.iso.datetime({ offset: true }),
});

/** A recent event the grade counted, which staff can excuse. */
export const GradeEventRowSchema = z.object({
  source: z.enum(EXCUSAL_SOURCES),
  sourceId: z.uuid(),
  at: z.iso.datetime({ offset: true }),
  label: z.string().max(200),
  excusalId: z.uuid().nullable(),
});

export const GradeDetailSchema = z.object({
  grade: PatientGradeSchema,
  history: z.array(GradeHistoryEntrySchema),
  excusals: z.array(ExcusalSchema),
  /** Missed, short-notice and late visits and payment problems of the last 24 months. */
  events: z.array(GradeEventRowSchema),
  /** Fees and deposits are never applied (Medicaid-only coverage). Not a grade input. */
  feeExempt: z.boolean(),
  canManage: z.boolean(),
  canExcuse: z.boolean(),
});
export type GradeDetail = z.infer<typeof GradeDetailSchema>;

export const OverrideBodySchema = z.object({
  letter: GradeLetterSchema,
  reason: z.string().trim().min(5).max(500),
  /** YYYY-MM-DD; omitted = until removed. */
  expiresOn: z.iso.date().optional(),
});
export const ReasonBodySchema = z.object({ reason: z.string().trim().min(5).max(500) });
export const ExcuseBodySchema = z.object({
  patientId: z.uuid(),
  source: z.enum(EXCUSAL_SOURCES),
  sourceId: z.uuid(),
  category: z.enum(EXCUSAL_CATEGORIES),
  reason: z.string().trim().min(5).max(500),
});

/* ------------------------------------------------------------------------ */
/* Policy evaluation                                                         */
/* ------------------------------------------------------------------------ */

export const PolicyEvaluateBodySchema = z.object({
  patientId: z.uuid(),
  locationId: z.uuid(),
  startsAt: z.iso.datetime({ offset: true }).optional(),
  lengthMin: z.number().int().min(5).max(1440).optional(),
  appointmentTypeId: z.uuid().optional(),
  /** Staff marked the visit an emergency: grades never restrict it. */
  emergency: z.boolean().optional(),
  channel: z.enum(['staff', 'online']).default('staff'),
});
export type PolicyEvaluateBody = z.infer<typeof PolicyEvaluateBodySchema>;

export const PolicyOutcomeSchema = z.object({
  rule: z.enum(POLICY_RULE_KEYS),
  /** restrict: the slot or channel is not offered; require: something is needed to book. */
  effect: z.enum(['restrict', 'require', 'info']),
  /** True only for online self-booking; staff can always go ahead with a reason. */
  blocking: z.boolean(),
  /** For staff: the rule and why it applies ("Attendance C: prime time is reserved..."). */
  staffText: z.string().max(400),
  /** For the patient: the office policy in neutral words, never the letter. */
  patientText: z.string().max(600).nullable(),
});
export type PolicyOutcomeView = z.infer<typeof PolicyOutcomeSchema>;

export const PolicySuppressionSchema = z.object({
  rule: z.enum(POLICY_RULE_KEYS),
  why: z.enum(['emergency', 'medicaid', 'legal_review', 'office_off', 'rule_off']),
  text: z.string().max(300),
});

export const PolicyEvaluationSchema = z.object({
  letter: GradeLetterSchema,
  enabled: z.boolean(),
  outcomes: z.array(PolicyOutcomeSchema),
  suppressed: z.array(PolicySuppressionSchema),
  cadence: z.enum(CONFIRMATION_CADENCES).nullable(),
  primeTime: z.boolean().nullable(),
});
export type PolicyEvaluation = z.infer<typeof PolicyEvaluationSchema>;

export const PolicyMessageBodySchema = z.object({
  patientId: z.uuid(),
  locationId: z.uuid(),
  rule: z.enum(POLICY_RULE_KEYS),
  appointmentId: z.uuid().optional(),
});
export const PolicyMessageResultSchema = z.object({
  id: z.uuid(),
  status: z.enum(['held', 'sent', 'failed']),
  body: z.string().max(600),
  heldReason: z.string().max(300).nullable(),
});

/* ------------------------------------------------------------------------ */
/* Distribution and disparity (reported, never an input)                     */
/* ------------------------------------------------------------------------ */

export const LetterCountsSchema = z.object({
  A: z.number().int(),
  B: z.number().int(),
  C: z.number().int(),
  D: z.number().int(),
  F: z.number().int(),
});

export const GradeDistributionSchema = z.object({
  total: z.number().int(),
  provisional: z.number().int(),
  overridden: z.number().int(),
  overall: LetterCountsSchema,
  attendance: LetterCountsSchema,
  financial: LetterCountsSchema,
  lastRunAt: z.iso.datetime({ offset: true }).nullable(),
});
export type GradeDistribution = z.infer<typeof GradeDistributionSchema>;

export const DisparityRowSchema = z.object({
  group: z.string().max(60),
  total: z.number().int(),
  counts: LetterCountsSchema,
  /** Share graded D or F. */
  lowShare: z.number(),
  /** lowShare divided by the lowest group's share (four-fifths style check on the favourable side). */
  ratioToBest: z.number().nullable(),
  flagged: z.boolean(),
});

export const DisparityReportSchema = z.object({
  quarter: z.string().regex(/^\d{4}Q[1-4]$/),
  generatedAt: z.iso.datetime({ offset: true }),
  ageBands: z.array(DisparityRowSchema),
  payer: z.array(DisparityRowSchema),
  note: z.string().max(600),
});
export type DisparityReport = z.infer<typeof DisparityReportSchema>;

/* ------------------------------------------------------------------------ */
/* Visible grades: words, the "why this grade" explanation, the overview     */
/* ------------------------------------------------------------------------ */

/** The word shown next to every letter, so colour is never the only signal. */
export const GRADE_WORD: Readonly<Record<GradeLetterValue, string>> = Object.freeze({
  A: 'Excellent',
  B: 'Good',
  C: 'Fair',
  D: 'Poor',
  F: 'At risk',
});

export const GRADE_COMPONENT_KEYS = [
  'no_shows',
  'late_cancels',
  'lateness',
  'confirmations',
  'consistency',
  'treatment',
  'recall',
  'call_tone',
  'balance_age',
  'payment_plans',
  'payment_failures',
  'statement_payment',
] as const;
export type GradeComponentKey = (typeof GRADE_COMPONENT_KEYS)[number];

/** Which sub-score a criterion belongs to (call tone and consistency count toward attendance). */
export const GRADE_FACTOR_DIMENSION: Readonly<
  Record<GradeComponentKey, 'attendance' | 'financial'>
> = Object.freeze({
  no_shows: 'attendance',
  late_cancels: 'attendance',
  lateness: 'attendance',
  confirmations: 'attendance',
  consistency: 'attendance',
  treatment: 'attendance',
  recall: 'attendance',
  call_tone: 'attendance',
  balance_age: 'financial',
  payment_plans: 'financial',
  payment_failures: 'financial',
  statement_payment: 'financial',
});

/** Staff-facing name of each criterion (hover, "why this grade", Settings). */
export const GRADE_FACTOR_LABEL: Readonly<Record<GradeComponentKey, string>> = Object.freeze({
  no_shows: 'No-shows',
  late_cancels: 'Short-notice cancellations',
  lateness: 'Arriving late',
  confirmations: 'Confirming visits',
  consistency: 'Appointment consistency',
  treatment: 'Treatment acceptance',
  recall: 'Recall (keeping up with check-ups)',
  call_tone: 'Upset or angry on calls',
  balance_age: 'Balance age',
  payment_plans: 'Payment plans',
  payment_failures: 'Declined cards, chargebacks, returned checks',
  statement_payment: 'Paying statements',
});

/** The groups the Settings page shows the criteria in. */
export const GRADE_FACTOR_GROUPS = [
  {
    id: 'attendance',
    label: 'Attendance',
    keys: ['no_shows', 'late_cancels', 'lateness', 'confirmations', 'consistency'],
  },
  { id: 'care', label: 'Treatment and recall', keys: ['treatment', 'recall'] },
  { id: 'calls', label: 'Conduct on calls', keys: ['call_tone'] },
  {
    id: 'payment',
    label: 'Payment behaviour',
    keys: ['balance_age', 'payment_plans', 'payment_failures', 'statement_payment'],
  },
] as const satisfies readonly { id: string; label: string; keys: readonly GradeComponentKey[] }[];

/** One line on what each criterion measures (Settings). */
export const GRADE_FACTOR_HINT: Readonly<Record<GradeComponentKey, string>> = Object.freeze({
  no_shows: 'Visits missed without notice.',
  late_cancels:
    'Cancelled under 48 hours, or broken in the PMS with no notice recorded. A PMS break two or more days ahead (cancelled in advance) counts too, never more than a short-notice one.',
  lateness: 'More than 10 minutes late (Lightboard or PMS arrival time).',
  confirmations: 'Confirms reminders before the day.',
  consistency:
    'Keeps a visit in each six-month period since their first visit. Needs at least two periods to judge; otherwise not counted.',
  treatment: 'Accepts the treatment plans presented (open over 90 days counts as not accepted).',
  recall: 'Recall not overdue, or already booked.',
  call_tone:
    'Callers who are repeatedly upset or angry on recorded calls (call analysis). One tense call never counts; anxious or confused callers and pain or emergency calls are never held against anyone. Patients without at least two analysed calls are not judged.',
  balance_age: "The account's balance over 60 and 90 days.",
  payment_plans: 'Missed or reversed payment-plan payments.',
  payment_failures: 'Declined cards, chargebacks, returned checks.',
  statement_payment: 'Days from a statement to the next payment.',
});

/**
 * The default weights (pg-2026.12.2). Within each sub-score the weights are
 * normalised to 100 points, so only their ratios matter.
 *
 * Owner demo 2026-11-02 (Dr. John): the grade was too lenient (about 90% A
 * or B, no F). Balance owed and no-shows are the two biggest factors,
 * short-notice cancellations next, then arriving late; treatment acceptance
 * counts only a little ("not them as a human being"); conduct on calls stays
 * a deduction. Calibrated on the production grade inputs of 2026-11-02
 * (docs/patient-grade.md).
 */
export const DEFAULT_GRADE_WEIGHTS: Readonly<Record<GradeComponentKey, number>> = Object.freeze({
  no_shows: 40,
  late_cancels: 25,
  lateness: 15,
  confirmations: 5,
  consistency: 5,
  treatment: 3,
  recall: 7,
  call_tone: 10,
  balance_age: 50,
  payment_plans: 20,
  payment_failures: 20,
  statement_payment: 10,
});

export const DEFAULT_GRADE_THRESHOLDS = Object.freeze({ A: 93, B: 85, C: 75, D: 65 });

const Weight = z.number().min(0).max(100);
export const GradeWeightsSchema = z.object(
  Object.fromEntries(GRADE_COMPONENT_KEYS.map((k) => [k, Weight])) as Record<
    GradeComponentKey,
    typeof Weight
  >,
);
export const GradeThresholdsSchema = z.object({
  A: z.number().min(1).max(100),
  B: z.number().min(1).max(100),
  C: z.number().min(1).max(100),
  D: z.number().min(1).max(100),
});

/**
 * A practice's grade rules: a weight per criterion (0 = off) and the score
 * each letter needs. Every sub-score keeps at least one criterion on, and
 * the letters step down (A above B above C above D).
 */
export const GradeCriteriaSchema = z
  .object({ weights: GradeWeightsSchema, thresholds: GradeThresholdsSchema })
  .superRefine((c, ctx) => {
    const t = c.thresholds;
    if (!(t.A > t.B && t.B > t.C && t.C > t.D)) {
      ctx.addIssue({
        code: 'custom',
        path: ['thresholds'],
        message: 'Each letter needs a lower score than the one above it (A > B > C > D).',
      });
    }
    for (const dim of ['attendance', 'financial'] as const) {
      const on = GRADE_COMPONENT_KEYS.filter(
        (k) => GRADE_FACTOR_DIMENSION[k] === dim && c.weights[k] > 0,
      );
      if (on.length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['weights'],
          message: `Keep at least one ${dim === 'attendance' ? 'attendance' : 'payment'} criterion on.`,
        });
      }
    }
  });
export type GradeCriteria = z.infer<typeof GradeCriteriaSchema>;

export function defaultGradeCriteria(): GradeCriteria {
  return { weights: { ...DEFAULT_GRADE_WEIGHTS }, thresholds: { ...DEFAULT_GRADE_THRESHOLDS } };
}

/**
 * Reads stored criteria leniently: a missing or malformed weight falls back
 * to its default (a criterion added later starts at its default weight), and
 * thresholds that do not step down fall back to the defaults.
 */
export function parseStoredCriteria(raw: unknown): GradeCriteria {
  const base = defaultGradeCriteria();
  if (!raw || typeof raw !== 'object') return base;
  const r = raw as { weights?: Record<string, unknown>; thresholds?: unknown };
  const weights = { ...base.weights };
  for (const k of GRADE_COMPONENT_KEYS) {
    const v = Weight.safeParse(r.weights?.[k]);
    if (v.success) weights[k] = v.data;
  }
  const t = GradeThresholdsSchema.safeParse(r.thresholds);
  const out = { weights, thresholds: t.success ? t.data : base.thresholds };
  return GradeCriteriaSchema.safeParse(out).success ? out : base;
}

export function isDefaultCriteria(c: GradeCriteria): boolean {
  return (
    GRADE_COMPONENT_KEYS.every((k) => c.weights[k] === DEFAULT_GRADE_WEIGHTS[k]) &&
    (['A', 'B', 'C', 'D'] as const).every((l) => c.thresholds[l] === DEFAULT_GRADE_THRESHOLDS[l])
  );
}

export const GradeCriteriaViewSchema = z.object({
  criteria: GradeCriteriaSchema,
  /** The practice has saved nothing (or saved exactly the defaults). */
  isDefault: z.boolean(),
  defaults: GradeCriteriaSchema,
  updatedAt: z.iso.datetime({ offset: true }).nullable(),
  updatedByName: z.string().max(200).nullable(),
  /** The nightly run that uses them (UTC). */
  nightlyAt: z.string().max(40),
});
export type GradeCriteriaView = z.infer<typeof GradeCriteriaViewSchema>;

export const GradeCriteriaPutSchema = z.object({ criteria: GradeCriteriaSchema });
export const GradeCriteriaPreviewBodySchema = z.object({ criteria: GradeCriteriaSchema });

export const GradeCriteriaPreviewSchema = z.object({
  /** Patients the nightly run grades (the estimate is scaled to this). */
  candidates: z.number().int(),
  /** Patients actually computed (a deterministic spread across the practice). */
  sampled: z.number().int(),
  /** Overall computed letters (overrides not applied), estimated for every candidate. */
  current: LetterCountsSchema,
  proposed: LetterCountsSchema,
  /** Estimated patients whose letter would improve / drop. */
  movedUp: z.number().int(),
  movedDown: z.number().int(),
  /** When the patient data behind the estimate was read (cached up to 10 minutes). */
  dataAt: z.iso.datetime({ offset: true }),
});
export type GradeCriteriaPreview = z.infer<typeof GradeCriteriaPreviewSchema>;

export const GradeCriteriaApplySchema = z.object({
  queued: z.boolean(),
  text: z.string().max(300),
});

export const GradeFactorSchema = z.object({
  key: z.enum(GRADE_COMPONENT_KEYS),
  label: z.string().max(80),
  /** The factor's share of its sub-score, in points out of 100. */
  maxPoints: z.number(),
  /** Points earned of `maxPoints`. */
  earnedPoints: z.number(),
  /** Points this factor cost the sub-score (0 when perfect). */
  pointsLost: z.number(),
  /** No history for this factor: counted at the neutral value. */
  neutral: z.boolean(),
  text: z.string().max(300),
});
export type GradeFactor = z.infer<typeof GradeFactorSchema>;

export const GradeDimensionExplanationSchema = z.object({
  dimension: z.enum(['attendance', 'financial']),
  label: z.string().max(40),
  letter: GradeLetterSchema,
  score: z.number().nullable(),
  provisional: z.boolean(),
  recovered: z.boolean(),
  factors: z.array(GradeFactorSchema).max(12),
});
export type GradeDimensionExplanation = z.infer<typeof GradeDimensionExplanationSchema>;

export const GRADE_TRENDS = ['improved', 'declined', 'same', 'new'] as const;

export const GradeEffectSchema = z.object({
  rule: z.enum(POLICY_RULE_KEYS),
  /** "Can book prime-time online", "Reminder cadence"... */
  label: z.string().max(80),
  /** "Yes", "No", "Extra reminder and a call at 48 hours"... */
  value: z.string().max(200),
  /** restricted: the rule limits or asks more of this patient; ok: nothing extra; off: rule not in use. */
  tone: z.enum(['ok', 'restricted', 'off']),
  note: z.string().max(300).nullable(),
});
export type GradeEffect = z.infer<typeof GradeEffectSchema>;

export const GradeExplanationSchema = z.object({
  grade: PatientGradeSchema,
  /** One plain-English sentence: "Grade C (Fair): attendance is the weaker side, mostly no-shows." */
  summary: z.string().max(400),
  /** Which sub-score sets the overall letter (the worse one), or null when tied / overridden. */
  drivenBy: z.enum(['attendance', 'financial']).nullable(),
  dimensions: z.array(GradeDimensionExplanationSchema).max(2),
  /** The formula run on today's data; differs from the stored grade until the nightly run. */
  live: z
    .object({ letter: GradeLetterSchema, differs: z.boolean(), text: z.string().max(300) })
    .nullable(),
  trend: z.object({
    direction: z.enum(GRADE_TRENDS),
    previousLetter: GradeLetterSchema.nullable(),
    changedAt: z.iso.datetime({ offset: true }).nullable(),
    text: z.string().max(200),
  }),
  effects: z.object({
    locationId: z.uuid().nullable(),
    officeName: z.string().max(200).nullable(),
    enabled: z.boolean(),
    items: z.array(GradeEffectSchema).max(10),
  }),
});
export type GradeExplanation = z.infer<typeof GradeExplanationSchema>;

/** Letters only, for list filters (up to 5000 patients per call). */
export const GradeLettersBodySchema = z.object({
  patientIds: z.array(z.uuid()).max(5000),
});
export const GradeLettersSchema = z.object({
  /** patientId -> effective letter; patients never graded read as B. */
  letters: z.record(z.string(), GradeLetterSchema),
});
export type GradeLetters = z.infer<typeof GradeLettersSchema>;

export const GradeOverviewQuerySchema = z.object({
  /** Window for movement and drops. */
  days: z.coerce.number().int().min(7).max(365).default(30),
  /** Only drops that landed on these letters (comma-separated). */
  to: z
    .string()
    .regex(/^[ABCDF](,[ABCDF]){0,4}$/)
    .optional(),
  locationId: z.uuid().optional(),
});
export type GradeOverviewQuery = z.infer<typeof GradeOverviewQuerySchema>;

export const GradeOverviewSchema = z.object({
  asOf: z.iso.datetime({ offset: true }),
  lastRunAt: z.iso.datetime({ offset: true }).nullable(),
  total: LetterCountsSchema,
  /** Graded patients by home office (effective letter). */
  offices: z.array(
    z.object({
      locationId: z.uuid(),
      name: z.string().max(200),
      counts: LetterCountsSchema,
      total: z.number().int(),
    }),
  ),
  /** Computed letter changes per week, oldest first. */
  movement: z.array(
    z.object({
      weekStart: z.iso.date(),
      improved: z.number().int(),
      declined: z.number().int(),
    }),
  ),
  /** Patients whose computed letter dropped in the window, latest drop per patient. */
  drops: z.array(
    z.object({
      patientId: z.uuid(),
      name: z.string().max(200),
      locationId: z.uuid().nullable(),
      officeName: z.string().max(200).nullable(),
      from: GradeLetterSchema,
      to: GradeLetterSchema,
      current: GradeLetterSchema,
      at: z.iso.datetime({ offset: true }),
    }),
  ),
  dropsTruncated: z.boolean(),
});
export type GradeOverview = z.infer<typeof GradeOverviewSchema>;

export const GradeDistributionQuerySchema = z.object({
  /** Only patients whose home office is this one (Settings preview). */
  locationId: z.uuid().optional(),
});
